import { Controller } from '@hotwired/stimulus'

// "¿Cuánto salió?" — the chips, the amount field, the yield line and the submit label all
// say the same number. A chip writes the field; typing clears the chips unless the typed
// value matches one. The server decides kept vs changed on its own; this is only display.
export default class extends Controller {
  static targets = ['chip', 'input', 'yield', 'submit', 'gapReveal', 'gapPrompt', 'reason']
  static values = { planned: Number, unit: String, submitTemplate: String, gapTemplate: String }

  connect () {
    this.render()
  }

  pick (event) {
    this.inputTarget.value = this.format(event.params.value)
    this.render()
  }

  typed () {
    this.render()
  }

  render () {
    const amount = this.amount()

    this.renderChips(amount)
    this.renderYield(amount)
    this.renderGap(amount)
    this.renderSubmit(amount)
  }

  // The gap is the merma, so it is shown rather than asked for. Hidden again when the yield comes
  // back up to plan, and the chosen reason is cleared with it — a cause left checked behind a
  // hidden block would post an explanation for a loss that no longer exists.
  renderGap (amount) {
    if (!this.hasGapRevealTarget) return

    const gap = amount === null ? 0 : Math.max(this.plannedValue - amount, 0)
    this.gapRevealTarget.classList.toggle('hidden', gap <= 0)
    this.gapRevealTarget.classList.toggle('flex', gap > 0)

    // `required` is toggled with the block: a required radio inside a hidden container makes the
    // browser refuse to submit the one-tap full batch, complaining about a control it cannot focus.
    this.reasonTargets.forEach(reason => { reason.required = gap > 0 })

    if (gap > 0) {
      if (this.hasGapPromptTarget && this.gapTemplateValue) {
        const amount = `${this.format(gap)} ${this.unitValue}`.trim()
        this.gapPromptTarget.textContent = this.gapTemplateValue.replace('%{amount}', amount)
      }
      return
    }

    this.reasonTargets.forEach(reason => { reason.checked = false })
  }

  renderChips (amount) {
    this.chipTargets.forEach(chip => {
      const value = Number(chip.getAttribute(`data-${this.identifier}-value-param`))
      chip.setAttribute('aria-pressed', String(amount !== null && value === amount))
    })
  }

  renderYield (amount) {
    if (!this.hasYieldTarget) return

    this.yieldTarget.textContent = amount === null || !this.plannedValue
      ? '—'
      : `${Math.round((amount / this.plannedValue) * 100)}%`
  }

  // An amount the field cannot read is not a yield anybody gave, so the submit closes rather
  // than recording the number it last saw.
  renderSubmit (amount) {
    if (!this.hasSubmitTarget) return

    this.submitTarget.disabled = amount === null
    if (amount === null || !this.submitTemplateValue) return

    const label = `${this.format(amount)} ${this.unitValue}`.trim()
    this.submitTarget.textContent = this.submitTemplateValue.replace('%{amount}', label)
  }

  // ⚠️ No separator substitution here, and none is wanted: this is a native `number` input, whose
  // `value` is a plain decimal string or the empty string — never the grouped or comma-written
  // form the typist saw. The screen that DOES take a typed separator is the capture sheet, whose
  // field is text for that reason.
  amount () {
    const raw = this.inputTarget.value.trim()
    if (raw === '') return null

    const value = Number(raw)
    return Number.isFinite(value) && value >= 0 ? value : null
  }

  format (value) {
    return String(Math.round(Number(value) * 100) / 100)
  }
}
